import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Users, Armchair, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { logError } from "@/lib/log";

const since = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : "");

export default function Tables() {
  const [tables, setTables] = useState(null);
  const [draft, setDraft] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/tables").then((r) => { setTables(r.data); setDraft(r.data); }).catch((e) => { logError("Tavoli:", e); toast.error(formatApiError(e.response?.data?.detail)); setTables([]); });
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, []);

  const toggle = async (t) => {
    try {
      const { data } = await api.put(`/tables/${t.id}/status`, { status: t.status === "occupied" ? "free" : "occupied" });
      setTables((ts) => ts.map((x) => (x.id === t.id ? data : x)));
      toast.success(data.status === "occupied" ? `Tavolo ${t.name} occupato` : `Tavolo ${t.name} liberato`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (draft.some((t) => !t.name.trim())) { toast.error("Dai un nome a ogni tavolo."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/tables", draft.map(({ id, name, seats }) => ({ id: id?.startsWith("new-") ? null : id, name, seats: Number(seats) || 2 })));
      setTables(data); setDraft(data); setEditing(false);
      toast.success("Tavoli salvati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!tables) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;
  const free = tables.filter((t) => t.status !== "occupied").length;

  return (
    <div className="space-y-6" data-testid="tables-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl">Tavoli</h1>
          <p className="text-gray-500 mt-1">Tocca un tavolo per segnarlo occupato o libero. I clienti vedono i tavoli <span className="text-[#4a8a56] font-medium">verdi</span> e possono prenotarli; si ordina solo da un tavolo occupato.</p>
          <p className="text-sm text-gray-400 mt-1" data-testid="tables-summary">{free} liberi · {tables.length - free} occupati · {tables.length} totali</p>
        </div>
        {!editing ? <Button onClick={() => setEditing(true)} variant="outline" className="rounded-full border-gray-300 bg-white" data-testid="tables-edit-btn"><Armchair className="h-4 w-4 mr-2" />Configura tavoli</Button>
          : <div className="flex gap-2"><Button onClick={() => { setDraft(tables); setEditing(false); }} variant="ghost" data-testid="tables-cancel-btn">Annulla</Button><Button onClick={save} disabled={saving} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white" data-testid="tables-save-btn"><Save className="h-4 w-4 mr-2" />{saving ? "Salvo…" : "Salva"}</Button></div>}
      </div>

      {editing ? (
        <div className="rounded-2xl mo-card p-5 space-y-3" data-testid="tables-editor">
          {draft.map((t, i) => (
            <div key={t.id} className="flex items-center gap-2">
              <Input value={t.name} onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Nome (es. 7, Veranda 2)" className="h-10 flex-1" data-testid={`table-name-${i}`} />
              <div className="flex items-center gap-1 text-sm text-gray-500"><Users className="h-4 w-4" /><Input type="number" min="1" max="50" value={t.seats} onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, seats: e.target.value } : x)))} className="h-10 w-20" data-testid={`table-seats-${i}`} /></div>
              <Button variant="ghost" size="icon" onClick={() => setDraft((d) => d.filter((_, j) => j !== i))} data-testid={`table-remove-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setDraft((d) => [...d, { id: `new-${Date.now()}`, name: String(d.length + 1), seats: 2, status: "free" }])} className="rounded-full border-dashed border-gray-300 bg-white" data-testid="table-add-btn"><Plus className="h-4 w-4 mr-1" />Aggiungi tavolo</Button>
        </div>
      ) : tables.length === 0 ? (
        <div className="rounded-2xl mo-card p-10 text-center text-gray-500" data-testid="tables-empty">Nessun tavolo configurato. Premi "Configura tavoli" per aggiungerli.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="tables-grid">
          {tables.map((t) => {
            const occ = t.status === "occupied";
            return (
              <button key={t.id} onClick={() => toggle(t)} className={`rounded-2xl p-4 text-left border-2 transition-[transform,background-color] active:scale-95 ${occ ? "bg-red-50 border-red-300" : "bg-[#5aa469]/10 border-[#5aa469]"}`} data-testid={`table-${t.id}`}>
                <div className="flex items-center justify-between"><span className="font-display font-bold text-2xl">{t.name}</span><span className={`h-3 w-3 rounded-full ${occ ? "bg-red-500" : "bg-[#5aa469]"}`} /></div>
                <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1"><Users className="h-3 w-3" />{t.seats} posti</p>
                <p className={`text-xs mt-2 font-medium ${occ ? "text-red-700" : "text-[#3f7a4a]"}`}>{occ ? `Occupato${t.guest ? ` · ${t.guest}` : ""}${t.party_size ? ` (${t.party_size})` : ""}${t.since ? ` · dalle ${since(t.since)}` : ""}` : "Libero"}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
